<script setup>
import { ref, computed, reactive, watch, onMounted, readonly } from 'vue';
import { useWidgets } from '@store/widgets';
import { Group, NewBadge, InputLabel, InputGroup, Text, Button, Toggle, Tooltip, Textarea, ColorPicker, ProBadge, MultiSelect, Select } from '@components';
import API from '@util/API'
import { useTranslation } from '../../composables/useTranslation';

const { t } = useTranslation();

const $widget = useWidgets();
const isLocked = computed(() => !formychat_admin_vars.is_premium)

const icon = computed(() => $widget.currentWidget.config?.icon || {});
const form = computed(() => $widget.currentWidget.config?.form || {});
const target = computed(() => $widget.currentWidget.config?.target || {})
const integrations = computed(() => {
    // Ensure integrations object exists on the store
    if (!$widget.currentWidget.config.integrations) {
        $widget.currentWidget.config.integrations = {}
    }
    return $widget.currentWidget.config.integrations
})

const fluentCRM = computed(() => {
    // Ensure fluent-crm object and its fields_mapping exist on the store
    if (!integrations.value['fluent-crm']) {
        integrations.value['fluent-crm'] = { fields_mapping: [ { from: 'email', to: 'email' } ] }
    } else if (!Array.isArray(integrations.value['fluent-crm'].fields_mapping)) {
        integrations.value['fluent-crm'].fields_mapping = [ { from: 'email', to: 'email' } ]
    }
    return integrations.value['fluent-crm']
})

const mailchimp = computed(() => {
    // Ensure mailchimp object and its fields_mapping exist on the store
    if (!integrations.value['mailchimp']) {
        integrations.value['mailchimp'] = {
            enabled: false,
            fields_mapping: [],
            lists: [],
            tags: [],
            skip_if_exists: false
        }
    } else if (!Array.isArray(integrations.value['mailchimp'].fields_mapping)) {
        integrations.value['mailchimp'].fields_mapping = []
    }
    return integrations.value['mailchimp']
})

const pages = computed(() => $widget.contents.pages ? $widget.contents.pages.map(p => ({ ...p, label: (p.label || t('untitled_page')) })) : [])
const posts = computed(() => $widget.contents.posts ? $widget.contents.posts.map(p => ({ ...p, label: (p.label || t('untitled_post')) })) : [])

// Ensure pro-only flags remain disabled when not premium
watch(isLocked, (locked) => {
    if (locked) {
        form.value.google_analytics = false
        form.value.fluent_crm = false
    }
}, { immediate: true })

import { DotLottieVue } from '@lottiefiles/dotlottie-vue'
const animationPath = formychat_admin_vars.public + '/js/animation.json';

const routeToEdit = () => {
    $widget.states.showSuccess = false
    $widget.currentWidget.isNew = false
    $widget.editWidget($widget.currentWidget)
}

const state = reactive({
    is_loading_fluentcrm: false,
    is_fetching_fluentcrm_meta: false,
    is_fetching_mailchimp_meta: false,
})
const additional = ref(formychat_admin_vars.additional || {})

const installFluentCRM = async () => {
    state.is_loading_fluentcrm = true
    // Make a request to integration install.
    const response = await API.post(`integrations/install`, {
        integration_id: 'fluent-crm',
        action: 'install'
    })

    state.is_loading_fluentcrm = false

    if (response.data.success) {
        additional.value.fluentcrm.is_installed = true
        additional.value.fluentcrm.is_active = true
        additional.value.fluentcrm.is_enabled = true

        refresh_fluentcrm_meta()
    }
}

const mailchimp_notice = computed(() => {
    // Check if integration is turned off first
    if (!additional.value.mailchimp || !additional.value.mailchimp.is_enabled) {
        return {
            title: t('mailchimp_module_not_active'),
            desc: t('activate_mailchimp_first'),
            button: {
                label: t('activate_mailchimp_arrow'),
                onClick: async () => {
                    state.is_fetching_mailchimp_meta = true
                    const response = await API.post(`integrations`, {
                        integration_id: 'mailchimp',
                        enabled: true
                    })
                    state.is_fetching_mailchimp_meta = false
                    if (response.data && response.data.success) {
                        additional.value.mailchimp.is_enabled = true
                        refresh_mailchimp_meta()
                    }
                }
            }
        }
    }
    // Check if API key is not connected (no audiences loaded)
    if (!mailchimp_lists.value.length) {
        return {
            title: t('mailchimp_api_not_connected'),
            desc: t('connect_mailchimp_in_integrations'),
            button: {
                label: t('go_to_mailchimp_module'),
                link: '/wp-admin/admin.php?page=formychat-integrations#/'
            }
        }
    }
    return null
})

const fluentcrm_notice = computed(() => {
    if (!additional.value.fluentcrm.is_installed) {
        return {
            title: t('fluentcrm_not_installed'),
            desc: t('install_fluentcrm_first'),
            button: {
                label: t('install_fluentcrm_arrow'),
                onClick: installFluentCRM
            }
        }
    }
    if (!additional.value.fluentcrm.is_active) {
        return {
            title: t('fluentcrm_not_active'),
            desc: t('activate_fluentcrm_plugin'),
            button: {
                label: t('activate_fluentcrm_arrow'),
                onClick: installFluentCRM
            }
        }
    }
    if (!additional.value.fluentcrm.is_enabled) {
        return {
            title: t('fluentcrm_not_enabled'),
            desc: t('enable_fluentcrm_integration'),
            button: {
                label: t('enable_fluentcrm_arrow'),
                onClick: async () => {
                    state.is_loading_fluentcrm = true
                    // Make a request to integrations to update integration to enabled.
                    const response = await API.post(`integrations`, {
                        integration_id: 'fluent-crm',
                        enabled: true
                    })

                    state.is_loading_fluentcrm = false

                    if (response.data && response.data.success) {
                        additional.value.fluentcrm.is_enabled = true

                        refresh_fluentcrm_meta()
                    }
                }
            }
        }
    }

    return null
})

const form_field_options = computed(() => {
    let result = []
    for (const [name, field] of Object.entries($widget.fields)) {
        result.push({ label: field, value: name })
    }
    return result
})

// Get Mailchimp fields for each mapping row, excluding already mapped fields
const getMailchimpFieldOptions = (currentMap) => {
    // Get all currently mapped field values (except the current one)
    const mappedFields = mailchimp.value.fields_mapping
        .filter(m => m !== currentMap)
        .map(m => m.to)
        .filter(to => to) // Remove empty values

    // Filter fields based on whether this is the EMAIL mapping or not
    return mailchimp_fields.value.filter(field => {
        const fieldValue = field.value || field.key

        // If this is the readonly EMAIL mapping, only show EMAIL
        if (currentMap.readonly && currentMap.to === 'EMAIL') {
            return fieldValue === 'EMAIL'
        }

        // For other mappings:
        // 1. Exclude EMAIL field
        // 2. Exclude already mapped fields
        return fieldValue !== 'EMAIL' && !mappedFields.includes(fieldValue)
    })
}

// Check if Mailchimp ADDRESS fields are incomplete
const mailchimp_address_incomplete = computed(() => {
    const mappedFields = (mailchimp.value.fields_mapping || [])
        .map(m => m.to)
        .filter(to => to && to.startsWith('ADDRESS.'))

    if (mappedFields.length === 0) {
        return false
    }

    // Required ADDRESS fields
    const requiredFields = ['ADDRESS.addr1', 'ADDRESS.city', 'ADDRESS.state', 'ADDRESS.zip']
    const hasAllRequired = requiredFields.every(field => mappedFields.includes(field))

    return !hasAllRequired
})

// Get FluentCRM fields for each mapping row, excluding already mapped fields
const getFluentCRMFieldOptions = (currentMap) => {
    // Get all currently mapped field values (except the current one)
    const mappedFields = fluentCRM.value.fields_mapping
        .filter(m => m !== currentMap)
        .map(m => m.to)
        .filter(to => to) // Remove empty values

    // Filter fields based on whether this is the email mapping or not
    return fluentcrm_fields.value.filter(field => {
        const fieldValue = field.value || field.key

        // If this is the readonly email mapping, only show email
        if (currentMap.readonly && currentMap.to === 'email') {
            return fieldValue === 'email'
        }

        // For other mappings:
        // 1. Exclude email field
        // 2. Exclude already mapped fields
        return fieldValue !== 'email' && !mappedFields.includes(fieldValue)
    })
}

// Placeholder Lists/Tags; will be populated via REST later
const fluentcrm_lists = ref([])
const fluentcrm_tags = ref([])
const fluentcrm_fields = ref([])

// Mailchimp Audiences/Tags/Fields
const mailchimp_lists = ref([])
const mailchimp_tags = ref([])
const mailchimp_fields = ref([])

const add_field_map = () => {
    // Safety: ensure mapping array exists before push
    if (!Array.isArray(fluentCRM.value.fields_mapping)) {
        fluentCRM.value.fields_mapping = []
    }
    fluentCRM.value.fields_mapping.push({ from: '', to: '' })
}
const remove_field_map = (index) => {
    fluentCRM.value.fields_mapping.splice(index, 1)
}

const add_mailchimp_field_map = () => {
    if (!Array.isArray(mailchimp.value.fields_mapping)) {
        mailchimp.value.fields_mapping = []
    }
    mailchimp.value.fields_mapping.push({ from: '', to: '' })
}
const remove_mailchimp_field_map = (index) => {
    mailchimp.value.fields_mapping.splice(index, 1)
}

const refresh_fluentcrm_meta = async () => {

    // If not installed or activated.
    if (!additional.value.fluentcrm.is_installed
        || !additional.value.fluentcrm.is_active
        || !additional.value.fluentcrm.is_enabled) {
        return
    }

    try {

        state.is_fetching_fluentcrm_meta = true

        const response = await API.get(`integrations/fluentcrm_meta`)

        state.is_fetching_fluentcrm_meta = false

        if (response.data && response.data.success && response.data) {
            const data = response.data

            // Update lists
            fluentcrm_lists.value = data.lists || []

            // Update tags  
            fluentcrm_tags.value = data.tags || []

            // Update CRM field options
            fluentcrm_fields.value = data.fields || []

            initFluentCRMFieldsMapping()
        }
    } catch (error) {
        console.error('Error fetching FluentCRM meta:', error)
    }

}

const normalize_key = (key) => String(key || '').toLowerCase().trim()
const guess_email_field = () => {
    const keys = Object.keys($widget.fields || {})
    if (!keys.length) return ''

    const normalized = keys.map(k => ({ original: k, norm: normalize_key(k) }))

    // Exact match
    const exact = normalized.find(k => k.norm === 'email')
    if (exact) return exact.original

    // Starts/ends with email
    const edge = normalized.find(k => k.norm.startsWith('email') || k.norm.endsWith('email'))
    if (edge) return edge.original

    // Contains email
    const contains = normalized.find(k => k.norm.indexOf('email') !== -1)
    if (contains) return contains.original

    // Heuristic for 'mail' but avoid provider substrings
    const mail = normalized.find(k => k.norm.includes('mail') && !k.norm.includes('gmail') && !k.norm.includes('hotmail') && !k.norm.includes('yahoo'))
    if (mail) return mail.original

    return ''
}

const initFluentCRMFieldsMapping = () => {

    // Ensure array
    fluentCRM.value.fields_mapping = Array.isArray(fluentCRM.value.fields_mapping) ? fluentCRM.value.fields_mapping : []

    // If empty, seed with intelligent default
    if (fluentCRM.value.fields_mapping.length === 0) {
        fluentCRM.value.fields_mapping.push({
            from: guess_email_field(),
            to: 'email',
            readonly: true
        })
    }

}

const refresh_mailchimp_meta = async (force_refresh = false) => {
    try {
        state.is_fetching_mailchimp_meta = true

        const url = force_refresh
            ? 'integrations/mailchimp_meta?force_refresh=1'
            : 'integrations/mailchimp_meta'
        const response = await API.get(url)

        state.is_fetching_mailchimp_meta = false

        if (response.data && response.data.success) {
            const data = response.data

            // Update lists
            mailchimp_lists.value = data.lists || []

            // Update tags
            mailchimp_tags.value = data.tags || []

            // Update Mailchimp field options
            mailchimp_fields.value = data.fields || []

            // Ensure EMAIL field is always available in the dropdown
            const hasEmail = mailchimp_fields.value.some(field => field.key === 'EMAIL' || field.value === 'EMAIL')
            if (!hasEmail) {
                mailchimp_fields.value.unshift({
                    key: 'EMAIL',
                    label: t('email'),
                    value: 'EMAIL',
                    type: 'email',
                    required: true
                })
            }

            initMailchimpFieldsMapping()
        }
    } catch (error) {
        console.error('Error fetching Mailchimp meta:', error)
        state.is_fetching_mailchimp_meta = false
    }
}

const initMailchimpFieldsMapping = () => {
    // Ensure array
    mailchimp.value.fields_mapping = Array.isArray(mailchimp.value.fields_mapping) ? mailchimp.value.fields_mapping : []

    // Find if EMAIL mapping already exists
    let hasEmailMapping = false
    for (const map of mailchimp.value.fields_mapping) {
        if (map.to === 'EMAIL') {
            map.readonly = true
            hasEmailMapping = true
            break
        }
    }

    // If no EMAIL mapping exists, add it at the beginning
    if (!hasEmailMapping) {
        mailchimp.value.fields_mapping.unshift({
            from: guess_email_field(),
            to: 'EMAIL',
            readonly: true
        })
    }
}

const handleMailchimpButtonClick = () => {
    if (mailchimp_notice.value.button.onClick) {
        mailchimp_notice.value.button.onClick()
    } else if (mailchimp_notice.value.button.link) {
        window.location.href = mailchimp_notice.value.button.link
    }
}

onMounted(() => {
    $widget.fetchFields()
    refresh_fluentcrm_meta()

    // Try to load Mailchimp data if enabled
    if (additional.value.mailchimp && additional.value.mailchimp.is_enabled) {
        refresh_mailchimp_meta()
    }
})

</script>
<template>
    <div class="w-full h-full space-y-8">
        <div>
            <Group :title="t('triggers')" :gap="false">
                <Toggle class="pt-1" v-model="$widget.currentWidget.is_active">{{ t('activate_widget') }}</Toggle>
                <!-- widget is turned on  -->
                <div class="space-y-5 bg-gray-50 p-5" v-if="$widget.currentWidget.is_active">
                    <InputGroup>
                        <Toggle v-model="icon.has_delay"><strong>{{ t('time_delay') }}</strong></Toggle>
                        <div class="transition" :class="{ 'opacity-60 pointer-events-none': !icon.has_delay }"> {{ t('display_after') }} <Text class="w-16 inline mx-2" v-model="icon.delay" type="number" min="0" max="3600"
                                placeholder="3" /> {{ t('seconds_when_page_loaded') }} </div>
                    </InputGroup>
                    <InputGroup>
                        <div class="flex items-center gap-2">
                            <Toggle v-model="icon.show_after_scroll"><strong>{{ t('page_scroll') }}</strong> </Toggle>

                        </div>
                        <div class="transition" :class="{ 'opacity-60 pointer-events-none': !icon.show_after_scroll }">
                            {{ t('display_after') }} <Text class="w-16 inline mx-2" v-model="icon.scroll_to" type="number" min="0"
                                max="100" placeholder="25" /> % {{ t('scrolling_on_the_page') }} </div>
                    </InputGroup>
                    <InputGroup>
                        <div class="flex items-center gap-2">
                            <Toggle v-model="icon.show_on_exit"><strong>{{ t('exit_intent') }}</strong> </Toggle>

                        </div>
                        <div class="transition"> {{ t('display_when_visitor_leave') }} </div>
                    </InputGroup>
                </div>
                <!-- when widget is turned off  -->
                <div v-else
                    class="bg-gray-50 border border-slate-100 rounded-lg py-5 px-6 gap-2 inline-flex items-center justify-start w-fit  text-gray-600">
                    <svg xmlns="http://www.w3.org/2000/svg" class="fill-current  text-yellow-500 w-4"
                        viewBox="0 0 16 16">
                        <path
                            d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                    </svg> {{ t('widget_turned_off') }}
                </div>
            </Group>
        </div>
        <Group>
            <template #title>
                <div class="flex items-center gap-3"> {{ t('targets') }}
                    <ProBadge />
                </div>
            </template>
            <div class="flex items-start gap-5"
                :class="{ 'opacity-70 formychat-locked child:pointer-events-none': isLocked }">
                <InputLabel class="w-40">{{ t('exclude_pages') }}</InputLabel>
                <InputGroup class="space-y-2">
                    <MultiSelect :options="pages" v-model="target.exclude_pages"
                        :class="{ ' opacity-60 pointer-events-none': target.exclude_all_pages }" />
                    <Toggle v-model="target.exclude_all_pages">{{ t('exclude_all_pages_except') }}</Toggle>
                    <MultiSelect :options="pages" v-model="target.exclude_all_pages_except"
                        :class="{ ' opacity-50 pointer-events-none': !target.exclude_all_pages }" />
                </InputGroup>
            </div>
            <div class="flex items-start gap-5"
                :class="{ 'opacity-70 formychat-locked child:pointer-events-none': isLocked }">
                <InputLabel class="w-40">{{ t('exclude_posts') }}</InputLabel>
                <InputGroup class="space-y-2">
                    <MultiSelect :options="posts" v-model="target.exclude_posts" :placeholder="t('select_posts')"
                        :class="{ ' opacity-60 pointer-events-none': target.exclude_all_posts }" />
                    <Toggle v-model="target.exclude_all_posts">{{ t('exclude_all_posts_except') }}</Toggle>
                    <MultiSelect :options="posts" v-model="target.exclude_all_posts_except" :placeholder="t('select_posts')"
                        :class="{ ' opacity-50 pointer-events-none': !target.exclude_all_posts }" />
                </InputGroup>
            </div>
        </Group>
        <Group :title="t('integrations')" :gap="false">

            <!-- Google Analytics -->
            <InputGroup>
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <Toggle :locked="isLocked" v-model="form.google_analytics"
                        :hints="t('google_analytics_hint')"> {{ t('google_analytics') }}
                    </Toggle>
                    <a href="https://wppool.dev/docs/how-to-set-up-google-analytics-on-formychat-view-form-conversions/" target="_blank" class="flex items-center gap-1 bg-gray-100 border border-gray-200 hover:bg-gray-50 transition text-gray-700 px-3 py-1.5 rounded-full text-xs font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                        </svg>
                        {{ t('documentation') }}
                    </a>
                </div>
            </InputGroup>

            <!-- FluentCRM -->
            <InputGroup>
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <div class="flex items-center gap-2">
                        <Toggle :locked="isLocked" v-model="fluentCRM.enabled"
                            :hints="t('fluentcrm_automation_hint')">
                            {{ t('send_leads_to_fluentcrm') }} </Toggle>
                        <NewBadge />
                    </div>
                    <a href="https://www.youtube.com/watch?v=5VFeUIqw3cg" target="_blank" class="flex items-center gap-1 bg-gray-100 border border-gray-200 hover:bg-gray-50 transition text-gray-700 px-3 py-1.5 rounded-full text-xs font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="16" height="16" viewBox="0 0 48 48">
                            <path fill="#FF3D00" d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z"></path>
                            <path fill="#FFF" d="M20 31L20 17 32 24z"></path>
                        </svg>
                        {{ t('tutorial') }}
                    </a>
                </div>
                <div v-if="fluentCRM.enabled" class="mt-2">
                    <!-- If  fluent crm is not enabled -->
                    <div v-if="fluentcrm_notice"
                        class="bg-amber-50 border border-amber-100 rounded-lg p-5 flex items-start justify-between gap-6">
                        <div class="flex items-start gap-3 text-gray-700">
                            <div>
                                <svg xmlns="http://www.w3.org/2000/svg" class="fill-current text-yellow-500 w-5 mt-0.5"
                                    viewBox="0 0 16 16">
                                    <path
                                        d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                                </svg>
                            </div>
                            <div class="space-y-1">
                                <div class="font-semibold">{{ fluentcrm_notice.title }}</div>
                                <div class="text-gray-500 text-sm">{{ fluentcrm_notice.desc }}</div>
                            </div>
                        </div>
                        <div v-if="fluentcrm_notice.button" class="shrink-0">
                            <Button @click.prevent="fluentcrm_notice.button.onClick" class="text-sm" :class="{
                                'opacity-50 pointer-events-none': state.is_loading_fluentcrm,
                            }">
                                <div v-if="state.is_loading_fluentcrm"
                                    class="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin">
                                </div>
                                <span v-else>{{ fluentcrm_notice.button.label }}</span>
                            </Button>
                        </div>
                    </div>
                    <!-- FluentCRM Settings for the widget  -->
                    <div v-else class="w-content bg-gray-50 rounded p-5 mt-4 space-y-6">
                        <div class="flex items-center gap-3">
                            <h3 class="text-gray-700 font-semibold text-lg">{{ t('fluentcrm_settings') }}</h3>
                            <a href="https://www.youtube.com/watch?v=bjJcWoY-8zA&list=PLd6WEu38CQSyebRIikg4qX54h-R1n35TG" target="_blank"
                                class="bg-gray-200 border flex items-center gap-1 border-gray-200 hover:bg-transparent transition text-gray-700 pl-2 pr-4 py-1 rounded-full text-xs"><svg
                                    xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="18" height="18"
                                    viewBox="0 0 48 48">
                                    <path fill="#FF3D00"
                                        d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z">
                                    </path>
                                    <path fill="#FFF" d="M20 31L20 17 32 24z"></path>
                                </svg> {{ t('tutorial') }}</a>
                        </div>
                        <div class="grid gap-6">
                            <div class="grid md:grid-cols-[220px_1fr] items-center gap-4">
                                <InputLabel>{{ t('fluentcrm_lists_label') }}</InputLabel>
                                <div class="relative flex items-center gap-2">
                                    <MultiSelect :options="fluentcrm_lists" :placeholder="t('select_lists_placeholder')"
                                        v-model="fluentCRM.lists"
                                        :class="{ 'opacity-50 pointer-events-none': state.is_fetching_fluentcrm_meta }" />
                                    <button @click.prevent="refresh_fluentcrm_meta"
                                        class="opacity-80 hover:opacity-100 transition"
                                        :class="{ 'animate-spin opacity-75 pointer-events-none': state.is_fetching_fluentcrm_meta }">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5"
                                            viewBox="0 0 16 16">
                                            <path
                                                d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9" />
                                            <path fill-rule="evenodd"
                                                d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div class="grid md:grid-cols-[220px_1fr] items-center gap-4">
                                <InputLabel>{{ t('fluentcrm_tags_label') }}</InputLabel>
                                <div class="relative flex items-center gap-2">
                                    <MultiSelect :options="fluentcrm_tags" :placeholder="t('select_tag_placeholder')"
                                        v-model="fluentCRM.tags"
                                        :class="{ 'opacity-50 pointer-events-none': state.is_fetching_fluentcrm_meta }" />
                                    <button @click.prevent="refresh_fluentcrm_meta"
                                        class="opacity-80 hover:opacity-100 transition"
                                        :class="{ 'animate-spin opacity-75 pointer-events-none': state.is_fetching_fluentcrm_meta }">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5"
                                            viewBox="0 0 16 16">
                                            <path
                                                d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9" />
                                            <path fill-rule="evenodd"
                                                d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="space-y-3">
                            <div class="text-gray-700 font-semibold">{{ t('map_fields') }}</div>
                            <div class="rounded-lg bg-white p-4 border border-gray-200 space-y-3">
                                <span name="fluentcrm_email_mapping"></span>
                                <div v-if="$widget.errors.fluentcrm_email_mapping" class="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-2">
                                    {{ t('map_email_fluentcrm_error') }}
                                </div>
                                <div class="flex items-center gap-2 text-gray-400 text-xs mb-2">
                                    <div class="w-full">{{ t('your_form_fields') }}</div>
                                    <div class="w-full">{{ t('fluentcrm_field_label') }}</div>
                                    <div class="w-[100px] flex h-1"></div>
                                </div>
                                <div class="flex items-center gap-3" v-for="(map, idx) in fluentCRM.fields_mapping"
                                    :key="idx">
                                    <Select size="w-full" :options="form_field_options" v-model="fluentCRM.fields_mapping[idx].from"
                                        :placeholder="t('select_field')" class="w-full"
                                        :class="{ 'opacity-75 pointer-events-none': state.is_fetching_fluentcrm_meta }" />
                                    <Select size="w-full" :options="getFluentCRMFieldOptions(map)" v-model="fluentCRM.fields_mapping[idx].to" :readonly="map.readonly"
                                        :placeholder="t('select_fluentcrm_field')" class="w-full"
                                        :class="{ 'opacity-75 pointer-events-none': state.is_fetching_fluentcrm_meta || map.readonly }" />
                                    <div class="w-20 flex items-center gap-2">
                                        <button @click.prevent="add_field_map"
                                            class=" bg-gray-100 hover:bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center"
                                            :class="{ 'opacity-50 pointer-events-none': state.is_fetching_fluentcrm_meta }">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                fill="currentColor" class="bi bi-plus-lg" viewBox="0 0 16 16">
                                                <path fill-rule="evenodd"
                                                    d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2" />
                                            </svg>
                                        </button>
                                        <button v-if="!map.readonly" @click.prevent="remove_field_map(idx)"
                                            class="text-gray-400 transition hover:text-red-500 flex-shrink-0"
                                            aria-label="Remove mapping"
                                            :class="{ 'opacity-50 pointer-events-none': state.is_fetching_fluentcrm_meta }">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                fill="currentColor" class="bi bi-trash-fill" viewBox="0 0 16 16">
                                                <path
                                                    d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0" />
                                            </svg> </button>
                                            <div class="flex w-4" v-else></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center gap-3">
                            <Toggle v-model="fluentCRM.skip_if_exists" :hints="t('skip_existing_contacts_hint')">{{ t('skip_if_contacts_exist') }}
                                FluentCRM </Toggle>
                        </div>
                    </div>
                </div>
            </InputGroup>

            <!-- Mailchimp -->
            <InputGroup>
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <div class="flex items-center gap-2">
                        <Toggle :locked="isLocked" v-model="mailchimp.enabled"
                            :hints="t('mailchimp_automation_hint')">
                            {{ t('send_leads_to_mailchimp') }}
                        </Toggle>
                        <NewBadge />
                    </div>
                    <a href="https://youtu.be/x70qZf2KWwg" target="_blank" class="flex items-center gap-1 bg-gray-100 border border-gray-200 hover:bg-gray-50 transition text-gray-700 px-3 py-1.5 rounded-full text-xs font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="16" height="16" viewBox="0 0 48 48">
                            <path fill="#FF3D00" d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z"></path>
                            <path fill="#FFF" d="M20 31L20 17 32 24z"></path>
                        </svg>
                        {{ t('tutorial') }}
                    </a>
                </div>
                <div v-if="mailchimp.enabled" class="mt-2">
                    <!-- If mailchimp is not enabled or API key not connected -->
                    <div v-if="mailchimp_notice"
                        class="bg-amber-50 border border-amber-100 rounded-lg p-5 flex items-start justify-between gap-6">
                        <div class="flex items-start gap-3 text-gray-700">
                            <div>
                                <svg xmlns="http://www.w3.org/2000/svg" class="fill-current text-yellow-500 w-5 mt-0.5"
                                    viewBox="0 0 16 16">
                                    <path
                                        d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                                </svg>
                            </div>
                            <div class="space-y-1">
                                <div class="font-semibold">{{ mailchimp_notice.title }}</div>
                                <div class="text-gray-500 text-sm">{{ mailchimp_notice.desc }}</div>
                            </div>
                        </div>
                        <div v-if="mailchimp_notice.button" class="shrink-0">
                            <Button @click.prevent="handleMailchimpButtonClick" class="text-sm"
                                :class="{ 'opacity-50 pointer-events-none': state.is_fetching_mailchimp_meta }"
                            >
                                <div v-if="state.is_fetching_mailchimp_meta"
                                    class="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin">
                                </div>
                                <span v-else>{{ mailchimp_notice.button.label }}</span>
                            </Button>
                        </div>
                    </div>
                    <!-- Mailchimp Settings for the widget  -->
                    <div v-else class="w-content bg-gray-50 rounded p-5 mt-4 space-y-6">
                        <div class="flex items-center gap-3">
                            <h3 class="text-gray-700 font-semibold text-lg">{{ t('mailchimp_settings') }}</h3>
                            <a href="https://mailchimp.com/help/"
                                target="_blank"
                                class="bg-gray-200 border flex items-center gap-1 border-gray-200 hover:bg-transparent transition text-gray-700 pl-2 pr-4 py-1 rounded-full text-xs">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                                </svg>
                                {{ t('documentation') }}
                            </a>
                        </div>

                        <!-- Lists and Tags -->
                        <div class="grid gap-6">
                            <div class="grid md:grid-cols-[220px_1fr] items-center gap-4">
                                <InputLabel>{{ t('mailchimp_audiences_label') }}</InputLabel>
                                <div class="relative flex items-center gap-2">
                                    <MultiSelect
                                        :options="mailchimp_lists"
                                        :placeholder="t('select_audiences_placeholder')"
                                        v-model="mailchimp.lists"
                                        :class="{ 'opacity-50 pointer-events-none': state.is_fetching_mailchimp_meta }"
                                    />
                                    <button
                                        @click.prevent="refresh_mailchimp_meta(true)"
                                        class="opacity-80 hover:opacity-100 transition"
                                        :class="{ 'animate-spin opacity-75 pointer-events-none': state.is_fetching_mailchimp_meta }"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5" viewBox="0 0 16 16">
                                            <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9" />
                                            <path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div class="grid md:grid-cols-[220px_1fr] items-center gap-4">
                                <InputLabel>{{ t('mailchimp_tags_label') }}</InputLabel>
                                <div class="relative flex items-center gap-2">
                                    <MultiSelect
                                        :options="mailchimp_tags"
                                        :placeholder="t('select_tags_placeholder')"
                                        v-model="mailchimp.tags"
                                        :class="{ 'opacity-50 pointer-events-none': state.is_fetching_mailchimp_meta }"
                                    />
                                    <button
                                        @click.prevent="refresh_mailchimp_meta(true)"
                                        class="opacity-80 hover:opacity-100 transition"
                                        :class="{ 'animate-spin opacity-75 pointer-events-none': state.is_fetching_mailchimp_meta }"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" class="fill-current w-5" viewBox="0 0 16 16">
                                            <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9" />
                                            <path fill-rule="evenodd" d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Field Mapping -->
                        <div class="space-y-3">
                            <div class="text-gray-700 font-semibold">{{ t('map_fields') }}</div>
                            <div class="rounded-lg bg-white p-4 border border-gray-200 space-y-3">
                                <span name="mailchimp_email_mapping"></span>
                                <div v-if="$widget.errors.mailchimp_email_mapping" class="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-2">
                                    {{ t('map_email_mailchimp_error') }}
                                </div>
                                <div class="flex items-center gap-2 text-gray-400 text-xs mb-2">
                                    <div class="w-full">{{ t('your_form_fields') }}</div>
                                    <div class="w-full">{{ t('mailchimp_field_label') }}</div>
                                    <div class="w-[100px] flex h-1"></div>
                                </div>
                                <div class="flex items-center gap-3" v-for="(map, idx) in mailchimp.fields_mapping" :key="idx">
                                    <Select
                                        size="w-full"
                                        :options="form_field_options"
                                        v-model="mailchimp.fields_mapping[idx].from"
                                        :placeholder="t('select_field')"
                                        class="w-full"
                                        :class="{ 'opacity-75 pointer-events-none': state.is_fetching_mailchimp_meta }"
                                    />
                                    <Select
                                        size="w-full"
                                        :options="getMailchimpFieldOptions(map)"
                                        v-model="mailchimp.fields_mapping[idx].to"
                                        :readonly="map.readonly"
                                        :placeholder="t('select_mailchimp_field')"
                                        class="w-full"
                                        :class="{ 'opacity-75 pointer-events-none': state.is_fetching_mailchimp_meta || map.readonly }"
                                    />
                                    <div class="w-20 flex items-center gap-2">
                                        <button
                                            @click.prevent="add_mailchimp_field_map"
                                            class="bg-gray-100 hover:bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center"
                                            :class="{ 'opacity-50 pointer-events-none': state.is_fetching_mailchimp_meta }"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-plus-lg" viewBox="0 0 16 16">
                                                <path fill-rule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2" />
                                            </svg>
                                        </button>
                                        <button
                                            v-if="!map.readonly"
                                            @click.prevent="remove_mailchimp_field_map(idx)"
                                            class="text-gray-400 transition hover:text-red-500 flex-shrink-0"
                                            aria-label="Remove mapping"
                                            :class="{ 'opacity-50 pointer-events-none': state.is_fetching_mailchimp_meta }"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash-fill" viewBox="0 0 16 16">
                                                <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0" />
                                            </svg>
                                        </button>
                                        <div class="flex w-4" v-else></div>
                                    </div>
                                </div>
                                <div v-if="mailchimp_address_incomplete" class="text-[13px] text-amber-700 bg-amber-50 border border-amber-300 rounded px-3 py-2 mt-3">
                                    <strong>{{ t('note') }}</strong> {{ t('address_mapping_note') }}
                                </div>
                            </div>
                        </div>

                        <!-- Skip existing contacts -->
                        <div class="flex items-center gap-3">
                            <Toggle v-model="mailchimp.skip_if_exists" :hints="t('skip_existing_contacts_hint')">
                                {{ t('skip_if_contacts_exist_mailchimp') }}
                            </Toggle>
                        </div>
                    </div>
                </div>
            </InputGroup>

        </Group>
        <transition name="form_slide">
            <div v-if="$widget.states.showSuccess"
                class="absolute -translate-y-8 sm:translate-y-0 w-full h-full left-0 top-0 bg-white z-50">
                <!-- close  -->
                <div
                    class="absolute -top-8 right-0 cursor-pointer w-12 h-12 flex items-center justify-center text-gray-300 hover:text-gray-700 transition">
                    <svg @click.prevent="routeToEdit" xmlns="http://www.w3.org/2000/svg" class="fill-current w-6"
                        viewBox="0 0 16 16">
                        <path
                            d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                    </svg>
                </div>
                <div class="flex flex-col gap-4 justify-center items-center h-96">
                    <DotLottieVue class="w-72" autoplay loop :src="animationPath" />
                    <h2 class="text-2xl text-gray-700">{{ $widget.currentWidget.isNew ? t('setup_finished') : t('widget_updated')}} </h2>
                    <div class="text-lg text-gray-500" v-if="$widget.currentWidget.is_active">{{ t('now_you_can_use') }} <a
                            href="https://go.wppool.dev/2rc7" target="_blank"
                            class="text-indigo-500 hover:text-indigo-400 transition">{{ t('formychat') }}</a></div>
                    <div class="flex items-center gap-3 justify-center my-6 font-medium">
                        <Button outline @click="routeToEdit" class="text-lg h-12 px-8">{{ t('done') }}</Button>
                        <Button v-if="isLocked" a href="https://go.wppool.dev/2rc7" target="_blank"
                            class="text-lg h-12 px-8" color="danger" outline
                            @click="$widget.states.showSuccess = false">{{ t('upgrade_all_features') }}</Button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>
